import React, { ReactNode } from "react";

interface PieChartSkeletonProps {
  children?: ReactNode;
}

export const PieChartSkeleton: React.FC<PieChartSkeletonProps> = ({ children }) => {
  const chartSize = 200;
  const centerX = chartSize / 2;
  const centerY = chartSize / 2;
  const outerRadius = 80;
  const innerRadius = 50;

  // Generate segments for donut chart
  const segments = [
    { startAngle: 0, endAngle: 280, delay: 0 },
    { startAngle: 280, endAngle: 360, delay: 0.1 },
  ];

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const describeDonutSegment = (
    x: number,
    y: number,
    outerRadius: number,
    innerRadius: number,
    startAngle: number,
    endAngle: number
  ) => {
    const outerStart = polarToCartesian(x, y, outerRadius, startAngle);
    const outerEnd = polarToCartesian(x, y, outerRadius, endAngle);
    const innerStart = polarToCartesian(x, y, innerRadius, startAngle);
    const innerEnd = polarToCartesian(x, y, innerRadius, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M",
      outerStart.x,
      outerStart.y,
      "A",
      outerRadius,
      outerRadius,
      0,
      largeArcFlag,
      1,
      outerEnd.x,
      outerEnd.y,
      "L",
      innerEnd.x,
      innerEnd.y,
      "A",
      innerRadius,
      innerRadius,
      0,
      largeArcFlag,
      0,
      innerStart.x,
      innerStart.y,
      "Z",
    ].join(" ");
  };

  return (
    <div
      style={{
        width: "100%",
        height: "360px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
        padding: "48px 0 12px 0",
        position: "relative",
        overflowY: "auto",
      }}
    >
      {/* Date range skeleton */}
      <div
        style={{
          height: "20px",
          width: "200px",
          backgroundColor: "var(--gray-200)",
          borderRadius: "4px",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      />

      {/* Pie chart skeleton */}
      <svg width={chartSize} height={chartSize} style={{ display: "block" }}>
        {segments.map((segment, index) => (
          <path
            key={`segment-${index}`}
            d={describeDonutSegment(centerX, centerY, outerRadius, innerRadius, segment.startAngle, segment.endAngle)}
            fill="var(--gray-200)"
            opacity="0.6"
            style={{
              animation: "pulse 1.5s ease-in-out infinite",
              animationDelay: `${segment.delay}s`,
            }}
          />
        ))}
      </svg>

      {/* Table skeleton */}
      <div style={{ width: "100%", maxWidth: "500px", display: "flex", flexDirection: "column", gap: "8px" }}>
        {/* Table header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            gap: "16px",
            paddingBottom: "8px",
            borderBottom: "1px solid var(--gray-200)",
          }}
        >
          {[80, 60, 40].map((width, i) => (
            <div
              key={`header-${i}`}
              style={{
                height: "14px",
                width: `${width}px`,
                backgroundColor: "var(--gray-200)",
                borderRadius: "2px",
                animation: "pulse 1.5s ease-in-out infinite",
                animationDelay: `${i * 0.05}s`,
              }}
            />
          ))}
        </div>

        {/* Table rows */}
        {Array.from({ length: 2 }, (_, i) => (
          <div
            key={`row-${i}`}
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                height: "14px",
                width: "120px",
                backgroundColor: "var(--gray-200)",
                borderRadius: "2px",
                animation: "pulse 1.5s ease-in-out infinite",
                animationDelay: `${i * 0.08}s`,
              }}
            />
            <div
              style={{
                height: "14px",
                width: "40px",
                backgroundColor: "var(--gray-200)",
                borderRadius: "2px",
                animation: "pulse 1.5s ease-in-out infinite",
                animationDelay: `${i * 0.08 + 0.05}s`,
              }}
            />
            <div
              style={{
                height: "14px",
                width: "35px",
                backgroundColor: "var(--gray-200)",
                borderRadius: "2px",
                animation: "pulse 1.5s ease-in-out infinite",
                animationDelay: `${i * 0.08 + 0.1}s`,
              }}
            />
          </div>
        ))}
      </div>

      {/* Children overlay */}
      {children && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {children}
        </div>
      )}

      {/* CSS animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 0.3;
          }
        }
      `}</style>
    </div>
  );
};
